"use client";

import React, { useMemo } from "react";
import classNames from "classnames";

import { Select } from "@/components/atoms";
import { Switch } from "@/components/molecules/Switch/Switch";
import { DateSelectorProps } from "./types";
import { DateRangePicker } from "@/components/molecules/DatePicker";
import { DateRange } from "../DatePicker/Preset";

const DATE_PRESET_OPTIONS = [
    { id: "yesterday", label: "Yesterday" },
    { id: "last_7_days", label: "Last 7 Days" },
    { id: "last_14_days", label: "Last 14 Days" },
    { id: "last_30_days", label: "Last 30 Days" },
    { id: "last_90_days", label: "Last 90 Days" },
    { id: "last_12_months", label: "Last 12 Months" },
    { id: "this_month", label: "This Month" },
    { id: "last_month", label: "Last Month" },
    { id: "this_year", label: "This Year" },
    { id: "last_year", label: "Last Year" },
    { id: "custom", label: "Custom Date" },
] as const;

export function DateSelector({
    usePageDate,
    onToggleUsePageDate,
    preset,
    onPresetChange,
    customRange,
    onCustomRangeChange,
}: DateSelectorProps) {
    const handlePresetChange = (value: number | string | null) => {
        if (!value) return;
        onPresetChange(value as DateSelectorProps["preset"]);
    };

    const dateRangeValue = useMemo(() => {
        if (customRange?.from && customRange?.to) {
            return { from: customRange.from, to: customRange.to };
        }
        return undefined;
    }, [customRange?.from, customRange?.to]);

    return (
        <div className="space-y-3">
            <div className="flex items-center justify-between">
                <div>
                    <p className="text-sm font-medium text-slate-700">Date Logic</p>
                    <p className="text-xs text-slate-500">{usePageDate ? "Displays data from dashboard dates" : "Set a date range"}</p>
                </div>
                <div className="flex items-center gap-2">
                    <Switch checked={usePageDate} onChange={onToggleUsePageDate} />
                </div>
            </div>

            {!usePageDate && (
                <div className="space-y-3">
                    <Select
                        label="Date preset"
                        options={DATE_PRESET_OPTIONS.map((option) => ({ id: option.id, label: option.label }))}
                        value={preset}
                        onChange={handlePresetChange}
                        placeholder="Select date range"
                        clearable={false}
                    />

                    {preset === "custom" && (
                        <div className={classNames('w-full') }>
                            <DateRangePicker
                                className="w-full"
                                showCompare={false}
                                showQuickSelect={false}
                                value={dateRangeValue}
                                onChange={(dateRange: DateRange | undefined) =>
                                    onCustomRangeChange({ from: dateRange?.from, to: dateRange?.to })
                                }
                            />
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
